/**
 * Remix deep-link intent (?remix=<appId>).
 *
 * Mirrors the campaign-referral persistence pattern (see `referral.ts`): an
 * external "Try in OptiDev" link points at `/dashboard/apps?remix=<appId>`. If
 * the visitor isn't signed in, ProtectedRoute bounces them through OAuth/signup
 * — and signup → onboarding can drop the query param. So we persist the intent
 * to localStorage before the redirect and replay it after auth (RemixIntentHandler),
 * the same way UTM/campaign data survives the OAuth round-trip.
 *
 * All operations are synchronous (localStorage) — no network calls.
 */
const REMIX_STORAGE_KEY = 'remix_intent';
// App ids are UUIDs; keep it bounded + permissive (alnum + hyphen) as a guard.
const APP_ID_RE = /^[a-zA-Z0-9-]{1,64}$/;

/**
 * Capture the `?remix=` app id from the URL and persist it.
 * Call before any OAuth redirect. Returns the id (fresh or already-persisted).
 */
export function captureAndPersistRemix(): string | null {
  const remix = new URLSearchParams(window.location.search).get('remix')?.trim();
  if (remix && APP_ID_RE.test(remix)) {
    localStorage.setItem(REMIX_STORAGE_KEY, remix);
    return remix;
  }
  // Fallback to a previously-persisted intent (survives the OAuth redirect).
  return getPersistedRemix();
}

/** Get the persisted remix intent (post-auth), or null. */
export function getPersistedRemix(): string | null {
  const stored = localStorage.getItem(REMIX_STORAGE_KEY);
  return stored && APP_ID_RE.test(stored) ? stored : null;
}

export function clearRemixIntent(): void {
  localStorage.removeItem(REMIX_STORAGE_KEY);
}
